import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Rating from '@mui/material/Rating'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReplyIcon from '@mui/icons-material/Reply'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'

import { opsApi, testimonialsApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { EmptyState, ErrorState } from '../../components/common/States'
import type { NoteStatus, Requester, SiteNoteDto } from '../../types/api'
import Avatar from '@mui/material/Avatar'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

/**
 * Guest reviews, and the notes the owner leaves on the site itself.
 *
 * They share a page because they are the same job seen from two sides: what guests say about
 * the stay, and what the owner wants changed about how the stay is described. The review build
 * of the site carries a floating note button, and this is where those notes arrive.
 */
const REQUESTER_LABEL: Record<Requester, string> = {
  LENA: 'Lena',
  MICHAEL: 'Michael',
  NAUM: 'Naum',
  OTHER: 'Someone else',
}

const NOTE_STATUSES: NoteStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED']

const STATUS_COLOR: Record<NoteStatus, 'warning' | 'info' | 'success'> = {
  OPEN: 'warning',
  IN_PROGRESS: 'info',
  RESOLVED: 'success',
}

export function AdminFeedbackPage() {
  const { t } = useTranslation()
  const { dateTimeAtEstate } = useFormatters()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState(0)
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [noteReplyTo, setNoteReplyTo] = useState<string | null>(null)
  const [noteReplyText, setNoteReplyText] = useState('')
  // The note waiting on "Move to bin?", and the one the Undo bar is offering back.
  const [confirmDelete, setConfirmDelete] = useState<SiteNoteDto | null>(null)
  const [justDeleted, setJustDeleted] = useState<SiteNoteDto | null>(null)
  const [binOpen, setBinOpen] = useState(false)

  const reviews = useQuery({ queryKey: ['admin', 'reviews'], queryFn: opsApi.reviews })
  const notes = useQuery({ queryKey: ['admin', 'siteNotes'], queryFn: opsApi.siteNotes })
  const binned = useQuery({ queryKey: ['admin', 'siteNotesDeleted'], queryFn: opsApi.deletedSiteNotes })
  const experiences = useQuery({
    queryKey: ['admin', 'testimonials'],
    queryFn: () => testimonialsApi.all('en'),
  })

  const invalidate = (key: string) => () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', key] })
    void queryClient.invalidateQueries({ queryKey: ['admin', 'ops', 'dashboard'] })
  }

  const publishExperience = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      testimonialsApi.setPublished(id, published),
    onSuccess: invalidate('testimonials'),
  })

  const deleteExperience = useMutation({
    mutationFn: (id: string) => testimonialsApi.remove(id),
    onSuccess: invalidate('testimonials'),
  })

  const publish = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      opsApi.publishReview(id, published),
    onSuccess: invalidate('reviews'),
  })

  const reply = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => opsApi.replyToReview(id, text),
    onSuccess: () => {
      invalidate('reviews')()
      setReplyingTo(null)
      setReplyText('')
    },
  })

  const setNoteStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: NoteStatus }) =>
      opsApi.setSiteNoteStatus(id, status),
    onSuccess: invalidate('siteNotes'),
  })

  const replyToNote = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => opsApi.replyToSiteNote(id, body),
    onSuccess: () => {
      invalidate('siteNotes')()
      setNoteReplyTo(null)
      setNoteReplyText('')
    },
  })

  const refreshNotes = () => {
    invalidate('siteNotes')()
    invalidate('siteNotesDeleted')()
  }

  const deleteNote = useMutation({
    mutationFn: (id: string) => opsApi.deleteSiteNote(id),
    onSuccess: (deleted) => {
      refreshNotes()
      setConfirmDelete(null)
      // Offer it straight back. The bin is the safety net; Undo is for the mis-tap you
      // notice a second later, which is most of them.
      setJustDeleted(deleted)
    },
  })

  const restoreNote = useMutation({
    mutationFn: (id: string) => opsApi.restoreSiteNote(id),
    onSuccess: () => {
      refreshNotes()
      setJustDeleted(null)
    },
  })

  const openNotes = (notes.data ?? []).filter((note) => note.status !== 'RESOLVED')
  const allExperiences = experiences.data ?? []
  const waitingExperiences = allExperiences.filter((one) => !one.published).length

  /**
   * How many there are, and how many need the owner.
   *
   * The counts used to be the "needs you" number alone, which meant a tab holding one
   * published experience and nothing to do read "Experiences (0)" - indistinguishable from
   * empty. A tab says how much is behind it; the dot says how much of it is waiting.
   */
  const tabLabel = (label: string, total: number, waiting = 0) =>
    waiting > 0 ? `${label} (${total}) • ${waiting}` : `${label} (${total})`

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 3 }}>
        {t('admin.feedback')}
      </Typography>

      <Tabs value={tab} onChange={(_, value) => setTab(value as number)} sx={{ mb: 3 }}>
        <Tab label={tabLabel(t('admin.reviews'), reviews.data?.length ?? 0)} />
        <Tab
          label={tabLabel(t('admin.siteNotes'), notes.data?.length ?? 0, openNotes.length)}
        />
        <Tab
          label={tabLabel(t('admin.experiences'), allExperiences.length, waitingExperiences)}
        />
      </Tabs>

      {tab === 0 && (
        <>
          {reviews.isError && <ErrorState error={reviews.error} onRetry={() => void reviews.refetch()} />}
          {reviews.data?.length === 0 && (
            <EmptyState icon="💬" title={t('admin.noReviews')} body={t('admin.noReviewsBody')} />
          )}

          <Stack spacing={2}>
            {(reviews.data ?? []).map((review) => (
              <Paper
                key={review.id}
                elevation={0}
                sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 3 }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {review.guestName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {review.unitName} · {review.bookingReference} ·{' '}
                      {dateTimeAtEstate(review.createdAt)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={review.published ? 'success' : 'default'}
                    label={review.published ? t('admin.published') : t('admin.notPublished')}
                  />
                </Stack>

                {review.comment && (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {review.comment}
                  </Typography>
                )}

                {review.ownerReply && (
                  <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('admin.yourReply')}
                    </Typography>
                    <Typography variant="body2">{review.ownerReply}</Typography>
                  </Paper>
                )}

                {replyingTo === review.id ? (
                  <Stack spacing={1}>
                    <TextField
                      autoFocus
                      fullWidth
                      multiline
                      minRows={2}
                      value={replyText}
                      onChange={(event) => setReplyText(event.target.value)}
                      label={t('admin.yourReply')}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!replyText.trim() || reply.isPending}
                        onClick={() => reply.mutate({ id: review.id, text: replyText })}
                      >
                        {t('common.save')}
                      </Button>
                      <Button size="small" onClick={() => setReplyingTo(null)}>
                        {t('common.back')}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<ReplyIcon />}
                      onClick={() => {
                        setReplyingTo(review.id)
                        setReplyText(review.ownerReply ?? '')
                      }}
                    >
                      {review.ownerReply ? t('common.edit') : t('admin.reply')}
                    </Button>
                    <Button
                      size="small"
                      variant={review.published ? 'text' : 'contained'}
                      disabled={publish.isPending}
                      onClick={() =>
                        publish.mutate({ id: review.id, published: !review.published })
                      }
                    >
                      {review.published ? t('admin.unpublish') : t('admin.publish')}
                    </Button>
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>
        </>
      )}

      {tab === 1 && (
        <>
          {notes.isError && <ErrorState error={notes.error} onRetry={() => void notes.refetch()} />}
          {notes.data?.length === 0 && (
            <EmptyState icon="📝" title={t('admin.noNotes')} body={t('admin.noNotesBody')} />
          )}

          <Stack spacing={1.5}>
            {(notes.data ?? []).map((note) => {
              const resolved = note.status === 'RESOLVED'
              return (
                <Paper
                  key={note.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    // A verified request from one of the three is the thing to act on, and is
                    // the only kind that earns a coloured edge.
                    borderColor: resolved
                      ? 'divider'
                      : note.verified
                        ? 'primary.main'
                        : 'divider',
                    borderRadius: 3,
                    opacity: resolved ? 0.6 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip
                      size="small"
                      color={note.verified ? 'primary' : 'default'}
                      label={note.verified ? REQUESTER_LABEL[note.requester] : t('admin.unverified')}
                    />
                    <Chip size="small" variant="outlined" label={note.pagePath} />
                    {note.locale && <Chip size="small" label={note.locale.toUpperCase()} />}
                    <Chip
                      size="small"
                      color={STATUS_COLOR[note.status]}
                      label={t(`note.status${note.status}`)}
                    />
                  </Stack>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {note.note}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {(note.verified ? REQUESTER_LABEL[note.requester] : note.authorName) ||
                      t('admin.anonymous')}{' '}
                    · {dateTimeAtEstate(note.createdAt)}
                  </Typography>

                  {note.replies.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                      {note.replies.map((entry) => (
                        <Paper
                          key={entry.id}
                          elevation={0}
                          sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block">
                            {entry.author} · {dateTimeAtEstate(entry.createdAt)}
                          </Typography>
                          <Typography variant="body2">{entry.body}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  {noteReplyTo === note.id ? (
                    <Stack spacing={1}>
                      <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        value={noteReplyText}
                        onChange={(event) => setNoteReplyText(event.target.value)}
                        label={t('admin.whatChanged')}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!noteReplyText.trim() || replyToNote.isPending}
                          onClick={() => replyToNote.mutate({ id: note.id, body: noteReplyText })}
                        >
                          {t('admin.sendReply')}
                        </Button>
                        <Button size="small" onClick={() => setNoteReplyTo(null)}>
                          {t('common.back')}
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => {
                          setNoteReplyTo(note.id)
                          setNoteReplyText('')
                        }}
                      >
                        {t('admin.reply')}
                      </Button>
                      {NOTE_STATUSES.filter((option) => option !== note.status).map((option) => (
                        <Button
                          key={option}
                          size="small"
                          color="inherit"
                          disabled={setNoteStatus.isPending}
                          startIcon={option === 'RESOLVED' ? <CheckCircleIcon /> : undefined}
                          onClick={() => setNoteStatus.mutate({ id: note.id, status: option })}
                        >
                          {t(`note.move${option}`)}
                        </Button>
                      ))}
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setConfirmDelete(note)}
                      >
                        {t('common.delete')}
                      </Button>
                    </Stack>
                  )}
                </Paper>
              )
            })}
          </Stack>

          {/*
            The bin. Collapsed by default: it is a safety net, not part of the work, and a
            list of deleted notes open under the live ones would read as more to do.
          */}
          <Divider sx={{ my: 3 }} />
          <Button
            color="inherit"
            startIcon={<DeleteOutlineIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={{ transform: binOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }}
              />
            }
            onClick={() => setBinOpen((open) => !open)}
            sx={{ color: 'text.secondary' }}
          >
            {t('admin.bin')} ({binned.data?.length ?? 0})
          </Button>
          <Collapse in={binOpen}>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {binned.data?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('admin.binEmpty')}
                </Typography>
              )}
              {(binned.data ?? []).map((note) => (
                <Paper
                  key={note.id}
                  elevation={0}
                  sx={{ p: 2, border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 3 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip size="small" variant="outlined" label={note.pagePath} />
                    {note.locale && <Chip size="small" label={note.locale.toUpperCase()} />}
                    <Chip size="small" label={t(`note.status${note.status}`)} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {note.note}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {t('admin.deletedBy', {
                      who: note.deletedBy ?? '-',
                      when: note.deletedAt ? dateTimeAtEstate(note.deletedAt) : '-',
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RestoreFromTrashIcon />}
                    disabled={restoreNote.isPending}
                    onClick={() => restoreNote.mutate(note.id)}
                  >
                    {t('admin.restore')}
                  </Button>
                </Paper>
              ))}
            </Stack>
          </Collapse>

          <Dialog
            open={confirmDelete !== null}
            onClose={() => !deleteNote.isPending && setConfirmDelete(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{t('admin.deleteNoteTitle')}</DialogTitle>
            <DialogContent>
              {/* The note itself, so "which one?" is answered before the tap, not after. */}
              {confirmDelete && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {confirmDelete.pagePath}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {confirmDelete.note}
                  </Typography>
                </Paper>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('admin.deleteNoteBody')}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {/* Cancel first and focused: the safe answer is the one Enter gives. */}
              <Button autoFocus onClick={() => setConfirmDelete(null)} disabled={deleteNote.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={deleteNote.isPending}
                onClick={() => confirmDelete && deleteNote.mutate(confirmDelete.id)}
              >
                {t('admin.moveToBin')}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={justDeleted !== null}
            autoHideDuration={8000}
            onClose={(_, reason) => reason !== 'clickaway' && setJustDeleted(null)}
            message={t('admin.noteMovedToBin')}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={restoreNote.isPending}
                onClick={() => justDeleted && restoreNote.mutate(justDeleted.id)}
                sx={{ fontWeight: 700 }}
              >
                {t('admin.undo')}
              </Button>
            }
          />
        </>
      )}

      {tab === 2 && (
        <>
          {experiences.isError && <ErrorState error={experiences.error} />}
          {allExperiences.length === 0 && (
            <EmptyState icon="⭐" title={t('admin.noExperiences')} body={t('admin.noExperiencesBody')} />
          )}

          <Stack spacing={2}>
            {allExperiences.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: 1,
                  // Waiting to be read is the state that needs attention.
                  borderColor: item.published ? 'divider' : 'primary.main',
                  borderRadius: 3,
                }}
              >
                <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip
                    size="small"
                    color={item.published ? 'success' : 'warning'}
                    label={item.published ? t('admin.published') : t('admin.awaitingReview')}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={item.source === 'SUBMITTED' ? t('admin.fromGuest') : t('admin.fromConsole')}
                  />
                </Stack>

                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {item.quote}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar src={item.photoUrl ?? undefined} alt={item.authorName} sx={{ width: 44, height: 44 }}>
                    {item.authorName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.authorName}
                    </Typography>
                    {item.authorRole && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.authorRole}
                      </Typography>
                    )}
                    {item.rating != null && <Rating value={item.rating} readOnly size="small" />}
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    size="small"
                    variant={item.published ? 'text' : 'contained'}
                    disabled={publishExperience.isPending}
                    onClick={() =>
                      publishExperience.mutate({ id: item.id, published: !item.published })
                    }
                  >
                    {item.published ? t('admin.unpublish') : t('admin.publish')}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={deleteExperience.isPending}
                    onClick={() => deleteExperience.mutate(item.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}

      <Divider sx={{ mt: 4 }} />
    </Box>
  )
}
